import { useCallback, useState } from 'react';

import { useSession } from '@clerk/clerk-react';

import { updateMemberDepartments } from '../api'; // この Feature の API を使う

// フックが返す値の型
interface UseManageMemberDepartmentsResult {
  saving: boolean;
  message: { type: 'success' | 'error' | ''; text: string };
  saveAssignments: (
    membersToSave: MemberWithDepts[],
    changedIds: Set<string>
  ) => Promise<void>; // 保存関数
  // 必要に応じて他の状態や関数を公開
}

// コンポーネントから渡されるメンバー情報の型 (仮)
// OrganizationSettings.jsx の members state の型に合わせる
interface MemberWithDepts {
  userId: string;
  deptIds: string[];
  identifier?: string; // エラーメッセージ表示用
  // 他の必要なプロパティ
}

/**
 * メンバーの部署割り当てを管理・保存するためのロジックを提供するフック
 */
export function useManageMemberDepartments(): UseManageMemberDepartmentsResult {
  const { session } = useSession();
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{
    type: 'success' | 'error' | '';
    text: string;
  }>({ type: '', text: '' });

  const saveAssignments = useCallback(
    async (membersToSave: MemberWithDepts[], changedIds: Set<string>) => {
      // 元の handleSaveDeptAssignments のロジックをここに移動・調整
      if (!session || changedIds.size === 0) return;

      setSaving(true);
      setMessage({ type: '', text: '' });
      let successCount = 0;
      let errorCount = 0;
      const errorMessages: string[] = [];

      try {
        const token = await session.getToken();
        if (!token) throw new Error('Authentication token missing.');

        const updatePromises: Promise<unknown>[] = [];

        changedIds.forEach((userId) => {
          const memberToUpdate = membersToSave.find((m) => m.userId === userId);
          if (memberToUpdate) {
            const payload = {
              userId: memberToUpdate.userId,
              deptIds: memberToUpdate.deptIds || [],
            };
            // updateMemberDepartments は Promise<unknown> を返すと仮定
            updatePromises.push(updateMemberDepartments(payload, token));
          }
        });

        const results = await Promise.allSettled(updatePromises);

        results.forEach((result, index) => {
          const userId = Array.from(changedIds)[index];
          const memberWithError = membersToSave.find(
            (m) => m.userId === userId
          );

          if (result.status === 'fulfilled') {
            successCount++;
            console.log(`Successfully updated departments for user ${userId}`);
          } else {
            errorCount++;
            // エラーメッセージの取得を改善
            let errorMsg = '不明なエラー';
            if (result.reason instanceof Error) {
              // AxiosError などの場合、より詳細な情報を取得できる可能性がある
              // errorMsg = result.reason.response?.data?.error || result.reason.message;
              errorMsg = result.reason.message;
            } else if (typeof result.reason === 'string') {
              errorMsg = result.reason;
            }
            console.error(
              `Failed to update departments for user ${userId}:`,
              result.reason
            );
            errorMessages.push(
              `ユーザー (${memberWithError?.identifier || userId}) の更新失敗: ${errorMsg}`
            );
          }
        });

        if (errorCount === 0 && successCount > 0) {
          setMessage({
            type: 'success',
            text: `選択された ${successCount} 名のメンバーの所属部署を更新しました。`,
          });
          // changedIds のリセットなどは呼び出し元 (UI コンポーネント) で行うのが一般的
          // setIsDeptAssignmentChanged(false); // この state は UI 側に持つ
          // setChangedMemberIds(new Set()); // この state は UI 側に持つ

          // メッセージを自動で消す処理 (必要であれば)
          // setTimeout(() => {
          //   setMessage({ type: '', text: '' });
          // }, 3000);
        } else if (errorCount > 0) {
          setMessage({
            type: 'error',
            text: `${errorCount} 名の更新に失敗しました: ${errorMessages.join('; ')}`,
          });
        } else {
          // 更新対象がなかった場合など (通常は changedIds.size === 0 でガードされる)
          setMessage({
            type: '',
            text: '更新対象のメンバーがいませんでした。',
          });
        }
      } catch (error: unknown) {
        console.error('所属部署の保存中に予期せぬエラーが発生しました:', error);
        const errorMessage =
          error instanceof Error ? error.message : '不明なエラー';
        setMessage({
          type: 'error',
          text: `保存処理中にエラーが発生しました: ${errorMessage}`,
        });
      } finally {
        setSaving(false);
      }
    },
    [session]
  );

  return {
    saving,
    message,
    saveAssignments,
  };
}
